import { ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { UtilsService } from '@services/common/utils.service';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { ClientHttpService } from '@services/datasources/client-http.service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { ClientDto } from 'app/@types/dto/client-dto.model';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { ConfigService } from 'app/app-config.service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';
import { revealFormErrors } from 'app/main/helpers/form-validation';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';

interface DedicatedAdminUserGroupControls {
    lastname: FormControl<string | null>;
    firstname: FormControl<string | null>;
    role: FormControl<string | null>;
    email: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
    defaultLocale: FormControl<string | null>;
    client: FormControl<string | null>;
}

interface DedicatedAdminFormControls {
    userGroup: FormGroup<DedicatedAdminUserGroupControls>;
}

@Component({
    selector: 'app-dedidcated-admin-form',
    templateUrl: './dedidcated-admin-form.component.html',
    styleUrls: ['./dedidcated-admin-form.component.scss'],
    providers: [ConfirmationService],
    standalone: false,
})
export class DedidcatedAdminFormComponent implements OnInit, OnDestroy {
    public settingsForm!: FormGroup<DedicatedAdminFormControls>;
    public isLoading = false;
    public createMode = false;

    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;

    get userFormGroup(): FormGroup<DedicatedAdminUserGroupControls> {
        return this.settingsForm.controls.userGroup;
    }

    private ngUnsubscribe = new Subject<void>();
    private dedicatedAdmin?: UserInfoDto;
    public availableLanguages = this.configService.getConfig.supportedLanguages;
    public isPlatformAdmin = false;
    public clientOptions?: ClientDto[];

    constructor(
        private translateService: CustomTranslateService,
        private formBuilder: FormBuilder,
        private adminHttpCalls: AdminHTTPService,
        private clientHttpService: ClientHttpService,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private appRouting: AppRoutingService,
        public adminSignals: AdminDataSyncService,
        private cdr: ChangeDetectorRef,
        private utilsService: UtilsService,
        private configService: ConfigService,
        private router: Router,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.initFormControls();
    }

    ngOnInit(): void {
        // Handle initial state
        this.isLoading = true;
        this.userClaims$.subscribe((userClaims) => {
            if (userClaims.tasks.some((task) => task.name === 'dcarb_admin')) {
                this.isPlatformAdmin = true;
                // A platform admin attaches every new dedicated admin to a client.
                this.userFormGroup.controls.client.addValidators(Validators.required);
                this.userFormGroup.controls.client.updateValueAndValidity();
                this.loadClients();
            } else {
                this.handleRouteState();
            }
        });

        // Subscribe to router events for subsequent navigation
        this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.handleRouteState();
            }
        });
    }

    private loadClients(): void {
        this.clientHttpService
            .getClients()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (clients) => {
                    this.clientOptions = clients;
                    this.handleRouteState();
                },
            });
    }

    private handleRouteState(): void {
        this.isLoading = true;
        const dedicatedAdminIdParam: unknown = this.route.snapshot.queryParams['dedicatedAdminId'];
        const dedicatedAdminId = typeof dedicatedAdminIdParam === 'string' ? dedicatedAdminIdParam : undefined;
        this.createMode = !dedicatedAdminId;

        if (!this.createMode && dedicatedAdminId) {
            // Existing admin → view-only. Disable here, keyed off the route's dedicatedAdminId,
            // instead of the organisations-console selection — so opening an admin's profile
            // neither depends on nor changes the console state.
            this.userFormGroup.disable();
            this.loadAdminData(dedicatedAdminId);
        } else {
            this.settingsForm.reset();
            this.userFormGroup.enable();
            this.dedicatedAdmin = undefined;
            this.isLoading = false;
        }
    }

    private loadAdminData(adminId: string): void {
        this.adminHttpCalls
            .getUserById(adminId)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (adminDto) => {
                    this.settingsForm.reset();
                    this.dedicatedAdmin = adminDto;
                    this.initFormValues();
                    this.isLoading = false;
                },
            });
    }

    initFormControls(): void {
        this.settingsForm = new FormGroup<DedicatedAdminFormControls>({
            userGroup: new FormGroup<DedicatedAdminUserGroupControls>({
                lastname: new FormControl<string | null>(null, Validators.required),
                firstname: new FormControl<string | null>(null, Validators.required),
                role: new FormControl<string | null>(null),
                email: new FormControl<string | null>(null, [Validators.required, CustomValidators.emailValidator]),
                phoneNumber: new FormControl<string | null>(null, [
                    Validators.required,
                    CustomValidators.phoneNumberValidator,
                ]),
                defaultLocale: new FormControl<string | null>('fr'),
                client: new FormControl<string | null>(null),
            }),
        });

        this.userFormGroup.controls.email.valueChanges.subscribe((value) => {
            if (value) {
                const lowerCaseValue = value.toLowerCase();
                if (value !== lowerCaseValue) {
                    this.userFormGroup.controls.email.setValue(lowerCaseValue, { emitEvent: false });
                }
            }
        });
    }
    initFormValues(): void {
        const userGroup = this.userFormGroup;
        userGroup.controls.lastname.setValue(this.dedicatedAdmin?.lastname ?? null);
        userGroup.controls.firstname.setValue(this.dedicatedAdmin?.firstname ?? null);
        userGroup.controls.role.setValue(this.dedicatedAdmin?.role ?? null);
        userGroup.controls.email.setValue(this.dedicatedAdmin?.email ?? null);
        userGroup.controls.phoneNumber.setValue(this.dedicatedAdmin?.phone_number ?? null);
        userGroup.controls.defaultLocale.setValue(this.dedicatedAdmin?.locale ?? 'fr');
        const clientId = this.dedicatedAdmin?.clientId;
        if (clientId && this.clientOptions) {
            userGroup.controls.client.setValue(clientId);
        }
        userGroup.markAsPristine();
        userGroup.controls.client.disable();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    onSubmit() {
        if (!revealFormErrors(this.settingsForm, this.host.nativeElement)) {
            return;
        }
        const userGroup = this.userFormGroup;
        this.isLoading = true;

        const phoneNumberValue = userGroup.controls.phoneNumber.value ?? '';
        const clientIdValue = this.isPlatformAdmin ? (userGroup.controls.client.value ?? undefined) : undefined;

        this.adminHttpCalls
            .createDedicateAdmin({
                firstname: userGroup.controls.firstname.value ?? undefined,
                lastname: userGroup.controls.lastname.value ?? undefined,
                email: userGroup.controls.email.value ?? '',
                phone_number: phoneNumberValue.replace(/[^0-9+]/g, ''),
                locale: userGroup.controls.defaultLocale.value ?? 'fr',
                role: userGroup.controls.role.value ?? undefined,
                roles: [],
                clientId: clientIdValue,
            })
            .subscribe({
                next: (result) => {
                    this.adminSignals.udpateDedicatedAdminListSubject.next(true);
                    this.adminSignals.currentSelectedDedicatedAdminId.next(result.uid ?? undefined);
                    const lastname = result.lastname ?? '';
                    const firstname = result.firstname ?? '';
                    this.adminSignals.updateDedicatedAdminNameSubject.next(`${lastname} ${firstname}`);
                    this.adminSignals.getData();
                    if (result.uid) {
                        this.appRouting.navigateToDedicatedAdminList(result.uid);
                    }
                    this.settingsForm.markAsPristine();
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    delete(): void {
        combineLatest([
            this.translateService.get('common.confirm_message'),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm(
                destructiveConfirmation({
                    message: this.utilsService.handleTranslation(translations[0]),
                    header: this.utilsService.handleTranslation(translations[1]),
                    acceptLabel: this.utilsService.handleTranslation(translations[2]),
                    rejectLabel: this.utilsService.handleTranslation(translations[3]),
                    icon: 'ms ms-warning',
                    accept: () => {
                        if (this.dedicatedAdmin?.uid) {
                            this.adminHttpCalls
                                .deleteDedicatedAdmin(this.dedicatedAdmin.uid)
                                .pipe(takeUntil(this.ngUnsubscribe))
                                .subscribe({
                                    next: () => {
                                        this.adminSignals.udpateDedicatedAdminListSubject.next(true);
                                        this.adminSignals.currentSelectedDedicatedAdminId.next(undefined);
                                        this.adminSignals.getData();
                                        this.appRouting.navigateToNewDedicatedUser();
                                    },
                                });
                        }
                    },
                }),
            );
        });
    }
}
